import { apiClient } from './api';
import type { PaginatedResponse, PaginationParams } from '../types';

type PagePayload<T> = {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

function unwrapPage<T>(res: { success: boolean; data?: PagePayload<T>; message?: string; error?: string }): PaginatedResponse<T> {
  if (!res.success || !res.data) {
    throw new Error(res.message || res.error || 'خطا در دریافت داده');
  }
  return {
    data: res.data.data ?? [],
    total: res.data.total ?? 0,
    page: res.data.page ?? 1,
    limit: res.data.limit ?? 10,
    totalPages: res.data.totalPages ?? 1,
  };
}

export type ProductViewSort = 'most_views' | 'least_views' | 'newest' | 'oldest';

export type ProductViewStat = {
  productId: string;
  name: string;
  sku?: string;
  slug?: string;
  viewCount: number;
  uniqueVisitors: number;
  lifetimeViews: number;
  lastViewedAt: string;
  firstViewedAt: string;
};

export type ProductViewerRow = {
  visitorKey: string;
  userId?: string;
  customerName: string;
  customerPhone?: string;
  viewCount: number;
  productCount: number;
  lastViewedAt: string;
  firstViewedAt: string;
  isAnonymous?: boolean;
};

export type ProductViewerDetail = {
  visitorKey: string;
  userId?: string;
  customerName: string;
  customerPhone?: string;
  visitDate: string;
  viewedAt: string;
};

export type ViewerProductDetail = {
  productId: string;
  name: string;
  sku?: string;
  slug?: string;
  visitDate: string;
  viewedAt: string;
};

export const analyticsService = {
  async listProductViews(params: PaginationParams & { sort?: ProductViewSort }): Promise<PaginatedResponse<ProductViewStat>> {
    const res = await apiClient.get<PagePayload<ProductViewStat>>('/admin/analytics/product-views', {
      params: { page: params.page, limit: params.limit, sort: params.sort || 'most_views' },
    });
    return unwrapPage(res);
  },

  async listProductViewers(params: PaginationParams & { sort?: ProductViewSort }): Promise<PaginatedResponse<ProductViewerRow>> {
    const res = await apiClient.get<PagePayload<ProductViewerRow>>('/admin/analytics/product-viewers', {
      params: { page: params.page, limit: params.limit, sort: params.sort || 'most_views' },
    });
    return unwrapPage(res);
  },

  async listViewersForProduct(
    productId: string,
    params: PaginationParams & { sort?: ProductViewSort },
  ): Promise<PaginatedResponse<ProductViewerDetail>> {
    const res = await apiClient.get<PagePayload<ProductViewerDetail>>(
      `/admin/analytics/product-views/${encodeURIComponent(productId)}/viewers`,
      { params: { page: params.page, limit: params.limit, sort: params.sort || 'newest' } },
    );
    return unwrapPage(res);
  },

  async listProductsForViewer(
    visitorKey: string,
    params: PaginationParams & { sort?: ProductViewSort },
  ): Promise<PaginatedResponse<ViewerProductDetail>> {
    const res = await apiClient.get<PagePayload<ViewerProductDetail>>(
      `/admin/analytics/product-viewers/${encodeURIComponent(visitorKey)}/products`,
      { params: { page: params.page, limit: params.limit, sort: params.sort || 'newest' } },
    );
    return unwrapPage(res);
  },
};
